import context, { ILookupService, ServiceLookupModel, ServiceModel, ServiceStatus, ServiceType } from '@truckdown/systems';
import { ClaimModel, IUserManager, MeasureUnit, UpdateUserPreferencesModel, UserModel } from "@truckdown/accounts";
import { defineComponent, ref, Ref, computed, onMounted } from "vue";
import { formatRadius, isFleet, showMessage } from "../../../Utilities/Utilities";
import { SearchRadiusPreference } from '@truckdown/locations';

interface IData {
    isLoading: Ref<boolean>,
    userProfile: Ref<UserModel>,
    updateModel: Ref<UpdateUserPreferencesModel>,
    allServices: Ref<ServiceModel[]>,
    timeZones: Ref<string[]>,
    searchTermTimeZone: Ref<string>,
    selectedServices: Ref<ServiceModel[]>,
    searchTermService: Ref<string>,
    showAdvancedPreferences: Ref<boolean>
};

interface ISelectItem {
    value: string,
    display: string,
    terms?: string
};

const getSearchByDistance = function (userProfile: UserModel) {
    var searchByDistance = userProfile.claims.find(c => {
        return c.type == "search_distance_search";
    });

    return (searchByDistance?.value ?? "False") == "True";
}

const getDefaultSearchServicesCodes = function (userProfile: UserModel) {
    var searchDefaultServices = userProfile.claims.filter(c => {
        return c.type == "search_default_service" && c.value.length > 0;
    });

    var serviceCodes = [];
    for (var i = 0; i < searchDefaultServices.length; i++) {
        serviceCodes.push(searchDefaultServices[i].value);
    }

    return serviceCodes;
}

const getReceiveNewsletter = function (userProfile: UserModel) {
    var receiveNewsletters = userProfile.claims.find(c => {
        return c.type == "newsletter_group" && c.value == "marketing"
    });
    return receiveNewsletters != null;
}

const getReceiveReports = function (userProfile: UserModel) {
    var receiveReports = userProfile.claims.find(c => {
        return c.type == "newsletter_group" && c.value == "reports"
    });
    return receiveReports != null;
}

const getInitialData = function (props: any) {
    var userProfile = props.userProfile as UserModel;

    var searchPrivateRadius = userProfile.claims.find(c => {
        return c.type == "search_private_radius";
    });

    var searchPreferredRadius = userProfile.claims.find(c => {
        return c.type == "search_preferred_radius";
    });

    var showAdvancedPreferences = userProfile.claims.find(c => {
        return c.type == "search_advanced_options";
    });

    var privateRadius = (<any>SearchRadiusPreference)[searchPrivateRadius?.value ?? "hundred"];
    var preferredRadius = (<any>SearchRadiusPreference)[searchPreferredRadius?.value ?? "hundred"];

    var update = {
        id: userProfile.id,
        timeZone: userProfile.properties.timezone,
        measureUnit: userProfile.properties.measureUnit,
        receiveProductUpdates: getReceiveNewsletter(userProfile),
        receiveReports: getReceiveReports(userProfile),
        defaultServices: getDefaultSearchServicesCodes(userProfile),
        hideManagedServices: userProfile.settings.hide_managed_services == 'True',
        searchByDistance: getSearchByDistance(userProfile),
        searchPreferredRadius: preferredRadius,
        searchPrivateRadius: privateRadius
    } as UpdateUserPreferencesModel;

    return {
        isLoading: ref<boolean>(false),
        userProfile: ref<UserModel>(props.userProfile),
        updateModel: ref<UpdateUserPreferencesModel>(update),
        timeZones: ref<string[]>(props.timeZones),
        selectedServices: ref<ServiceModel[]>([]),
        allServices: ref<ServiceModel[]>(props.allServices),
        searchTermService: ref<string>(''),
        showAdvancedPreferences: ref<boolean>((showAdvancedPreferences?.value ?? "False") == "True" && isFleet())
    } as IData;
}


const getMethods = function (props: any, data: IData, ctx: any) {
    return {
        editUserPreferencesCancelClick: function (): void {
            ctx.emit(userCancelEventName);
        },
        editUserPreferencesSaveClick: async function (): Promise<void> {
            await saveUser(data, props, ctx);
        },
        selectTimeZone: function (timeZone: ISelectItem, ndx: number): void {
            data.updateModel.value.timeZone = timeZone.value;
        },
        selectService: function (service: string) {
            if (service != "") {
                data.updateModel.value.defaultServices.push(service);
            }
        },
        deselectService: function (service: string) {
            data.updateModel.value.defaultServices = data.updateModel.value.defaultServices.filter(s => s !== service);
        },
        measureUnitDisplay: function (measureUnit: MeasureUnit): string {
            return MeasureUnit[measureUnit];
        },
        searchDistanceDisplay: function (searchDistance: SearchRadiusPreference): string {
            return formatRadius(SearchRadiusPreference[searchDistance]);
        },
        serviceDisplay: function (serviceCode: string): string {
            var allServices = props.allServices as ServiceModel[];
            var service = allServices.filter(s => s.code == serviceCode);
            if (service.length == 1) {
                return service[0].name;
            } else {
                return serviceCode;
            }
        },
        getNotSelectedServices: function (): ServiceModel[] {
            var servicesToRemove = data.updateModel.value.defaultServices;
            var allServices = props.allServices as ServiceModel[];

            var notSelectedList = allServices.filter(service => !servicesToRemove.includes(service["code"]));

            if (data.searchTermService.value != '') {
                return notSelectedList.filter(service => service.name.toLowerCase().indexOf(data.searchTermService.value.toLowerCase()) > -1);
            } else {
                return notSelectedList;
            }
        }
    }
}

const saveUser = async function (data: IData, props: any, ctx: any): Promise<void> {
    if (!data.isLoading.value) {
        data.isLoading.value = true;

        try {
            let userManager = await context.getService<IUserManager>('IUserManager');

            var response = await userManager.updatePreferences(data.updateModel.value);

            if (!response || !response.ok) {
                console.log("Error updating user preferences: " + response.message);
                showMessage("Error updating user preferences.", "danger", true, true);
            } else {
                showMessage("User Preferences was successfully updated.", "success", true, true);
                ctx.emit(userUpdatedEventName, response.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            data.isLoading.value = false;
        }
    }
}

const userUpdatedEventName = 'update';
const userCancelEventName = 'cancel';

export default defineComponent({
    props: {
        userProfile: {
            type: Object as () => UserModel,
            required: true
        },
        allServices: {
            type: Object as () => ServiceModel[],
            required: true
        },
        timeZones: {
            type: Array as () => string[],
            required: false
        }
    },
    emits: [userUpdatedEventName, userCancelEventName],
    setup: function (props, ctx) {

        let data = getInitialData(props);
        let methods = getMethods(props, data, ctx);

        return {
            ...data,
            ...methods,
            MeasureUnit,
            SearchRadiusPreference
        };
    }
});